import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { MoveStatusRenderer } from 'shared/components/ui/MoveStatusRenderer/MoveStatusRenderer';
import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import { Switch } from 'shared/components/ui/switch/switch';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useGlobalStore } from 'shared/store/globalStore';
import { useRoomStore } from 'shared/store/roomStore';
import { useStartGameStore } from 'shared/store/start-game-store';

export const JoinGameStatus = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState({
    allowance: false,
    joining: false,
  });
  const { address } = useAccount();
  const { selectedCurrency } = useCurrenciesStore();
  const { bet } = useRoomStore();
  const [fullAllowance, setFullAllowance] = useState(true);
  const invitedBy = useGlobalStore((state) => state.invitedBy);
  const { activeRoomId } = useParams();

  const {
    fetchCurrentAllowance,
    currentAllowance,
    approveAllowance,
    joinRoom,
    isApproving,
    isFetching,
  } = useStartGameStore();
  const publicClient = usePublicClient();
  const walletClient = useWalletClient();
  useEffect(() => {
    if (publicClient && selectedCurrency && address) {
      fetchCurrentAllowance(address, selectedCurrency, publicClient);
    }
  }, [publicClient, selectedCurrency, address]);

  const approveAllowanceHandler = async (fullAllowance: boolean, bet: number) => {
    if (!walletClient || !publicClient || !selectedCurrency) return;
    setLoading({
      allowance: true,
      joining: false,
    });
    try {
      await approveAllowance({
        amount: +bet,
        currency: selectedCurrency,
        userAddress: address as `0x${string}`,
        walletClient,
        publicClient,
        fullAllowance,
        joinGame: false,
      });
    } catch (e) {
    } finally {
      setLoading({
        allowance: false,
        joining: false,
      });
    }
  };

  const joinGameHandler = async () => {
    if (!walletClient || !publicClient || !activeRoomId) return;

    setLoading({
      allowance: false,
      joining: true,
    });
    try {
      await joinRoom({
        roomId: BigInt(activeRoomId),
        referrer: invitedBy,
        walletClient,
        publicClient,
        userAddress: address as `0x${string}`,
      });
    } catch (e) {
    } finally {
      setLoading({
        allowance: false,
        joining: false,
      });
    }
  };

  const isJoiningDisabled = useMemo(
    () =>
      Number(currentAllowance) < +bet ||
      isApproving ||
      isFetching ||
      (selectedCurrency?.balance ?? 0) < +bet,
    [currentAllowance, bet, isApproving, isFetching, selectedCurrency],
  );

  if (!selectedCurrency) return null;
  return (
    <div>
      <MoveStatusRenderer title={t('modals.joinGameModal.start')} className="mb-2" />
      {Number(currentAllowance) <= +bet ? (
        <p className="text-center text-[12px] text-white md:text-[16px]">
          {t('modals.joinGameModal.pressAuthorize')}
        </p>
      ) : (
        <p className="text-center text-[12px] text-white md:text-[16px]">
          {t('modals.joinGameModal.pressStart')}
        </p>
      )}
      <AnimatedTransition show={Number(currentAllowance) <= +bet}>
        <div className="flex items-center gap-2 pt-4">
          <p className="mr-3 border-r-[1px] border-border pr-3 text-[14px] text-light-grape">
            {t('modals.setBet.plan')}
          </p>
          <Switch
            className="ml-2"
            onCheckedChange={() => {
              setFullAllowance(!fullAllowance);
            }}
            checked={fullAllowance}
          />
        </div>
      </AnimatedTransition>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <PrimaryButton
          text={'common.authorize'}
          disabled={Number(currentAllowance) >= +bet}
          onClick={() => {
            approveAllowanceHandler(fullAllowance, +bet);
          }}
          isLoading={loading.allowance}
        />
        <PrimaryButton
          text={'common.join'}
          disabled={isJoiningDisabled}
          onClick={joinGameHandler}
          isLoading={loading.joining}
        />
      </div>
    </div>
  );
};
